/**
 * The action registry — buttons that do something, without the spec containing
 * anything.
 *
 * Decision 0014 draws the boundary and it does not move:
 *
 *   **The spec may NAME an action from the registry. It may never CONTAIN one.**
 *
 * So a button is an affordance naming an action id, resolved here, executed by
 * the host with **declared effects**. That is the shape `MUTATIONS` already has
 * for read-write bindings under decision 0007 — a binding declares what it may
 * change and the runtime refuses anything undeclared — and it generalizes to
 * behaviour without modification.
 *
 * Two authorities, and only one of them is closed:
 *
 * - **The registry is open.** Actions are REGISTERED, not enumerated forever.
 *   A permanent core list would create a second authority alongside the open
 *   capability-module registry established in decision 0010, and the argument
 *   against that is one this project made itself.
 * - **The descriptor shape and the effect vocabulary are closed.** What must
 *   never open is the *shape* of what an action may declare and the set of
 *   effects it may claim. Which actions exist is a registry question, and
 *   registries grow.
 *
 * What a user "writes" when they build an automation is a composition of
 * registered actions, stored as data in the object store — never code in the
 * spec. `runAutomation` below executes such a composition, and there is
 * deliberately no way to express a branch, a loop or an expression in it. If
 * that turns out to be too little, the answer is another registered action, not
 * a language.
 */

import * as V from './vocabulary.js';
/**
 * What `invoke` needs to know, structurally.
 *
 * `TInvocation` is the schema's mutable form; the compiled IR carries a deeply
 * readonly one. Taking the readonly shape means both fit without a cast, and a
 * cast at a security boundary is exactly the kind of thing that stops being
 * checked.
 */
export interface InvocationLike {
  readonly action: string;
  readonly effects: readonly V.Effect[];
  readonly args?: readonly { readonly role?: V.Role; readonly valueFrom?: number }[];
}

export interface Refusal {
  readonly ok: false;
  readonly code: string;
  readonly message: string;
}

export type Outcome<T> = { readonly ok: true; readonly value: T } | Refusal;

const refuse = (code: string, message: string): Refusal => ({ ok: false, code, message });

/**
 * The only things an action can actually do.
 *
 * An action never touches a store, a DOM or a router. It is handed this, and
 * every method on it is gated by what the action declared — so "an action with
 * undeclared effects is refused" is enforced by there being no other door,
 * rather than by a check someone remembered to write at the call site.
 *
 * Every write goes through the host's own grant path. An action must not be a
 * way around the authorization an inline edit is subject to: the same store,
 * the same manifest, the same refusal.
 */
export interface Effects {
  setProperty(role: V.Role, value: unknown): Promise<void>;
  createObject(title: string): Promise<string>;
  navigate(via: V.Role): Promise<void>;
}

/** What the host supplies for one invocation. `subject` is opaque on purpose —
 *  the registry never learns an object's identity, per decision 0007. */
export interface ActionContext {
  readonly subject?: string;
  readonly args: readonly unknown[];
  readonly effects: Effects;
}

/**
 * Private values the host may use to resolve an invocation's positional
 * arguments.
 *
 * Neither source is serialized into the layout spec: roles are resolved from
 * the pressed button's subject, while `viewState` is indexed by the same
 * integer the query language uses. That is the whole point of the field —
 * `args[].valueFrom` is an INDEX into `spec.viewState`, exactly as
 * `Predicate.valueFrom` is, so a button and a filter read one control the same
 * way.
 */
export interface InvocationSources {
  readonly role: (role: V.Role) => unknown;
  readonly viewState: readonly unknown[];
  readonly viewStateCount: number;
}

/**
 * Resolve every declared argument at press time.
 *
 * The bounds check is repeated here deliberately: validation checks the
 * AUTHORED spec, and a host must not trust an IR or a stored automation merely
 * because it was valid once. A slot count can shrink between the spec being
 * written and the button being pressed.
 */
export function resolveInvocationArgs(
  inv: InvocationLike, sources: InvocationSources,
): Outcome<readonly unknown[]> {
  const out: unknown[] = [];
  for (const [i, arg] of (inv.args ?? []).entries()) {
    const hasRole = arg.role !== undefined;
    const hasValueFrom = arg.valueFrom !== undefined;
    if (hasRole === hasValueFrom) {
      return refuse(
        hasRole ? 'argument-multiple-sources' : 'argument-without-source',
        hasRole
          ? `argument ${i + 1} names both a role and a view-state slot`
          : `argument ${i + 1} names no value source`,
      );
    }
    if (hasValueFrom) {
      const index = arg.valueFrom!;
      if (!Number.isInteger(index) || index < 0 || index >= sources.viewStateCount) {
        return refuse('view-state-index',
          `argument ${i + 1} reads undeclared view-state slot ${index}`);
      }
      out.push(sources.viewState[index]);
    } else {
      out.push(sources.role(arg.role!));
    }
  }
  return { ok: true, value: out };
}

export interface ActionDescriptor {
  readonly name: string;
  readonly effects: readonly V.Effect[];
  readonly run: (ctx: ActionContext) => Promise<void>;
}

/**
 * Wraps `Effects` so a call an action did not declare cannot happen.
 *
 * The refusal is thrown rather than returned, because an action's `run` is
 * third-party code and a return value it can ignore is not a boundary. The
 * throw is caught in `invoke` and converted back into a refusal, so a caller
 * still sees a value rather than an exception.
 */
class UndeclaredEffect extends Error {
  constructor(readonly effect: V.Effect, readonly action: string) {
    super(`"${action}" attempted "${effect}", which it did not declare`);
    this.name = 'UndeclaredEffect';
  }
}

function gate(effects: Effects, declared: readonly V.Effect[], action: string): Effects {
  const allow = (e: V.Effect) => {
    if (!declared.includes(e)) throw new UndeclaredEffect(e, action);
  };
  return {
    async setProperty(role, value) { allow('set-property'); return effects.setProperty(role, value); },
    async createObject(title) { allow('create-object'); return effects.createObject(title); },
    async navigate(via) { allow('navigate'); return effects.navigate(via); },
  };
}

export class ActionRegistry {
  private readonly actions = new Map<string, ActionDescriptor>();

  /**
   * Validate a descriptor, then register it.
   *
   * Refuses rather than throws, so a capability module that ships one bad
   * action does not take the host down with it — the rest of its actions stay
   * registered and the bad one is simply not exposed.
   */
  register(d: ActionDescriptor): Outcome<string> {
    if (typeof d?.name !== 'string' || !V.ACTION_NAME.test(d.name)) {
      return refuse('bad-action-name',
        `"${String(d?.name)}" is not a namespaced action name`);
    }
    // A silent replace is a hijack: a module registered later could take over a
    // name the spec already points at, and every existing button would quietly
    // start doing something else.
    if (this.actions.has(d.name)) {
      return refuse('already-registered', `"${d.name}" is already registered`);
    }
    if (!Array.isArray(d.effects) || d.effects.length === 0) {
      return refuse('no-effects-declared',
        `"${d.name}" declares no effects; an action whose contract cannot be `
        + 'checked is not safer for being vague');
    }
    const unknown = d.effects.filter((e) => !(V.EFFECTS as readonly string[]).includes(e));
    if (unknown.length) {
      return refuse('unknown-effect',
        `"${d.name}" declares ${unknown.map((e) => `"${e}"`).join(', ')}, `
        + 'which is not in the effect vocabulary');
    }
    if (typeof d.run !== 'function') {
      return refuse('not-runnable', `"${d.name}" has no run function`);
    }
    this.actions.set(d.name, d);
    return { ok: true, value: d.name };
  }

  has(name: string): boolean { return this.actions.has(name); }

  /** What a host needs to render a button: whether it exists, and what it may
   *  do. Never the descriptor itself — handing out `run` would let a caller
   *  bypass the gate by calling it directly. */
  describe(name: string): Outcome<readonly V.Effect[]> {
    const d = this.actions.get(name);
    return d ? { ok: true, value: d.effects } : refuse('unknown-action', `"${name}" is not registered`);
  }

  /**
   * Run one invocation.
   *
   * The check that matters is the SUBSET one. The spec declares what this
   * button may do and the descriptor declares what the action does; the action
   * may not exceed the spec. The spec is the ceiling because it is the thing
   * that was reviewed, rendered and — with the action name stripped — pooled. A
   * registry that could widen it would make the spec's declaration decorative.
   */
  async invoke(inv: InvocationLike, ctx: ActionContext): Promise<Outcome<void>> {
    const d = this.actions.get(inv.action);
    if (!d) return refuse('unknown-action', `"${inv.action}" is not registered`);

    const exceeded = d.effects.filter((e) => !inv.effects.includes(e));
    if (exceeded.length) {
      return refuse('effect-not-declared-by-spec',
        `"${inv.action}" may ${exceeded.map((e) => `"${e}"`).join(', ')}, `
        + 'which this button did not declare');
    }

    try {
      await d.run({ ...ctx, effects: gate(ctx.effects, d.effects, d.name) });
      return { ok: true, value: undefined };
    } catch (e) {
      if (e instanceof UndeclaredEffect) {
        return refuse('undeclared-effect', e.message);
      }
      // An action that throws is a failed action, not a crashed host. The same
      // rule the pool gate states twice: a crash is not a refusal, so it is
      // turned into one here.
      return refuse('action-failed', e instanceof Error ? e.message : String(e));
    }
  }

  /**
   * Run a stored composition — "when this is pressed, do A then B".
   *
   * The composition is DATA: a list of invocations read out of the object
   * store. There is no branch, no loop and no expression, which is what keeps
   * it data rather than a small language nobody agreed to ship.
   *
   * Stops at the first refusal and reports which step. Continuing past one
   * would leave the user with half an automation applied and no way to know
   * which half.
   */
  async runAutomation(
    steps: readonly InvocationLike[], ctx: ActionContext,
    /**
     * Where each step's declared arguments come from.
     *
     * Without it a step that carries `args` is REFUSED rather than run with
     * whatever the caller's own `ctx.args` happened to hold. Every step used to
     * share one context whose `args` was `[]`, so a stored automation asking
     * for a role or a view-state slot silently ran with the action's default —
     * preserved perfectly through `parseAutomation` and then discarded at the
     * moment it mattered. Found by @Codex.
     *
     * Failing closed rather than resolving to nothing: an automation that says
     * "set status from slot 0" and quietly writes the default is a stored
     * instruction that does something else, which is worse than one that stops.
     */
    sources?: InvocationSources,
  ): Promise<Outcome<number>> {
    if (steps.length === 0) return refuse('empty-automation', 'an automation needs a step');
    if (steps.length > MAX_STEPS) {
      return refuse('automation-too-long',
        `an automation may have at most ${MAX_STEPS} steps`);
    }
    for (const [i, step] of steps.entries()) {
      let stepCtx = ctx;
      if (step.args?.length) {
        if (!sources) {
          return refuse('unresolvable-arguments',
            `step ${i + 1} of ${steps.length} declares arguments and this caller `
            + 'supplied no sources to resolve them from');
        }
        const args = resolveInvocationArgs(step, sources);
        if (!args.ok) return refuse(args.code, `step ${i + 1} of ${steps.length}: ${args.message}`);
        stepCtx = { ...ctx, args: args.value };
      }
      const r = await this.invoke(step, stepCtx);
      if (!r.ok) {
        return refuse(r.code, `step ${i + 1} of ${steps.length}: ${r.message}`);
      }
    }
    return { ok: true, value: steps.length };
  }
}

/** A bound, because a stored composition is user data and a runaway list is a
 *  denial of service against the user's own session. */
export const MAX_STEPS = 16;

export const isRefusal = (o: Outcome<unknown>): o is Refusal => o.ok === false;
